import React, { useEffect, useState } from 'react';
import { Button, Icon, Image, Label, Modal, Popup } from 'semantic-ui-react';
import moment from 'moment';
import { Post } from '../../types/Post';
import { normalizeId } from '../../../utils/normalizeId';
import { ResidentIdentityMap } from '../../hooks/useResidentIdentities';
import OrderCommentsSection from '../OrderStatus/OrderCommentsSection';
import { useFacility } from '../../contexts/facility/FacilityContext';
import { reportContent } from '../../services/Posts';
import sendToast from '../../../utils/Toast';
import ReportContentModal from '../ReportContentModal';

interface PostCardProps {
    post: Post;
    facilityId: string;
    currentResidentId: string;
    onEdit: (post: Post) => void;
    onDelete: (post: Post) => void;
    onToggleLike?: (post: Post) => void;
    /** Remove the post from the feed after a successful report. */
    onPostReported?: (post: Post) => void;
    onMentionClick?: (type: 'resident' | 'staff', id: string) => void;
    groupNameById?: Record<string, string>;
    residentIdentityById?: ResidentIdentityMap;
    // The group currently selected as a feed filter, if any — shown first in the tag.
    selectedGroupIds?: string[];
}

const MAX_CONTENT_LENGTH = 200;
const MENTION_REGEX = /@\[(.+?)\]\((staff|resident):(.+?)\)/g;

const stripMentions = (text: string) => text.replace(MENTION_REGEX, '@$1');

const renderContentWithMentions = (
    text: string,
    onMentionClick?: (type: 'resident' | 'staff', id: string) => void,
    maxVisibleLength?: number,
) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let visibleCount = 0;
    let truncated = false;
    const regex = new RegExp(MENTION_REGEX.source, 'g');
    let match;

    const appendText = (str: string): boolean => {
        if (maxVisibleLength === undefined) {
            parts.push(str);
            return false;
        }
        const remaining = maxVisibleLength - visibleCount;
        if (remaining <= 0) return true;
        if (str.length <= remaining) {
            parts.push(str);
            visibleCount += str.length;
            return false;
        }
        parts.push(str.slice(0, remaining) + '...');
        visibleCount += remaining;
        return true;
    };

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            if (appendText(text.slice(lastIndex, match.index))) {
                truncated = true;
                break;
            }
        }

        if (maxVisibleLength !== undefined && visibleCount >= maxVisibleLength) {
            truncated = true;
            break;
        }

        const [, name, type, id] = match;
        const displayText = `@${name}`;
        visibleCount += displayText.length;

        if (type === 'resident' && onMentionClick) {
            parts.push(
                <span
                    key={`mention-${match.index}`}
                    className="post-mention post-mention-clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onMentionClick('resident', id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onMentionClick('resident', id);
                        }
                    }}
                >
                    {displayText}
                </span>
            );
        } else {
            parts.push(
                <span key={`mention-${match.index}`} className="post-mention">{displayText}</span>
            );
        }
        lastIndex = regex.lastIndex;
    }

    if (!truncated && lastIndex < text.length) {
        appendText(text.slice(lastIndex));
    }

    return parts;
};

const PostCard = ({ post, facilityId, currentResidentId, onEdit, onDelete, onToggleLike, onPostReported, onMentionClick, groupNameById, residentIdentityById, selectedGroupIds }: PostCardProps) => {
    const [expanded, setExpanded] = useState(false);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [commentsExpanded, setCommentsExpanded] = useState(false);
    const [commentCount, setCommentCount] = useState(post.CommentCount || 0);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reporting, setReporting] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    // A post can only be reported once. Seeded from the post (defensive — reported posts are
    // normally filtered from the feed) and also set when the server says a race beat us to it.
    const [alreadyReported, setAlreadyReported] = useState(!!post.IsReported);
    const { facilityTimezone } = useFacility();

    // useState only seeds on mount, so a feed refetch (or a sibling card's mutation) left the
    // badge showing a stale count for as long as the card stayed mounted. Resync whenever the
    // server-side count changes; the comments section still owns the count while it is open.
    useEffect(() => {
        setCommentCount(post.CommentCount || 0);
    }, [post.CommentCount]);

    const isOwner = post.AuthorId === currentResidentId;
    const likeCount = post.LikeCount ?? 0;
    const likedByMe = post.likedByMe ?? false;
    const likers = post.likers ?? [];
    // LikeCount and likers are resolved by two independent backend queries, so
    // they can disagree (e.g. one query fails while the other succeeds). Treat
    // LikeCount as authoritative for "are there likes at all" so the summary
    // line and the hover tooltip never contradict each other.
    const hasLikes = likeCount > 0;
    // Author name/image on the post doc is denormalized at write time from the
    // resident's speak2 record; the map carries the social profile resolved by the
    // directory, which takes priority. Staff authors are never in the map.
    const authorIdentity = post.AuthorType === 'resident'
        ? residentIdentityById?.[normalizeId(post.AuthorId)]
        : undefined;
    const authorName = post.AuthorIsActive === false
        ? 'Former Resident'
        : (authorIdentity?.Name || post.AuthorName);
    const authorImage = post.AuthorIsActive === false
        ? '/avatar_placeholder.png'
        : (authorIdentity?.Image || post.AuthorImage || '/avatar_placeholder.png');
    const rawContent = post.Content || '';
    const strippedContent = stripMentions(rawContent);
    const canTruncate = strippedContent.length > MAX_CONTENT_LENGTH;
    const maxVisible = canTruncate && !expanded ? MAX_CONTENT_LENGTH : undefined;
    // Posts only carry group ids; resolve every one we have a name for, with the
    // currently filtered-on group (if any) surfaced first.
    const resolvedGroups = (post.Groups || [])
        .map(groupId => ({ id: normalizeId(groupId), name: groupNameById?.[normalizeId(groupId)] }))
        .filter((g): g is { id: string; name: string } => Boolean(g.name));
    const priorityGroupId = (selectedGroupIds || [])[0];
    const orderedGroups = priorityGroupId
        ? [...resolvedGroups].sort((a, b) => Number(b.id === priorityGroupId) - Number(a.id === priorityGroupId))
        : resolvedGroups;
    const groupTagName = orderedGroups[0]?.name;
    const extraGroupCount = orderedGroups.length - 1;

    const handleReport = () => {
        setMenuOpen(false);
        setReportModalOpen(true);
    };

    const submitReport = async (reason?: string) => {
        setReporting(true);
        try {
            const res = await reportContent({ facilityId, contentType: 'Post', contentId: post._id, reason, registrantId: currentResidentId });
            setAlreadyReported(true);
            setReportModalOpen(false);
            sendToast(res?.alreadyReported ? 'info' : 'success', res?.alreadyReported ? 'This post has already been reported' : 'Post reported');
            // Either way it's now under review and hidden from other feeds; drop it here too.
            onPostReported?.(post);
        } catch (e) {
            sendToast('error', e instanceof Error ? e.message : 'Failed to report post');
        } finally {
            setReporting(false);
        }
    };

    return (
        <div className="post-card">
            <div className="post-card-header">
                <Image src={authorImage} circular size="mini" />
                <div className="post-card-author-info">
                    <span className="post-card-author-name" title={authorName}>{authorName}</span>
                    <span className="post-card-timestamp">
                        <Icon name="clock outline" />
                        <span className="post-card-timestamp-text" title={moment(post.DateAdded).fromNow()}>
                            {moment(post.DateAdded).fromNow()}
                        </span>
                    </span>
                </div>
                {groupTagName && (
                    extraGroupCount > 0 ? (
                        <Popup
                            trigger={
                                <span
                                    className="post-card-group-tag"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.currentTarget.click();
                                        }
                                    }}
                                >
                                    {groupTagName} and {extraGroupCount} other{extraGroupCount > 1 ? 's' : ''}
                                </span>
                            }
                            on={['hover', 'click']}
                            position="bottom left"
                            size="mini"
                            className="post-card-groups-popup"
                            content={
                                <div className="post-card-groups-tooltip">
                                    {orderedGroups.map(g => (
                                        <div key={g.id} className="post-card-groups-tooltip-item">{g.name}</div>
                                    ))}
                                </div>
                            }
                        />
                    ) : (
                        <span className="post-card-group-tag" title={groupTagName}>{groupTagName}</span>
                    )
                )}
                {post.ApprovalStatus === 'pending' && (
                    <Label color="yellow" size="tiny">Pending Approval</Label>
                )}
                {isOwner ? (
                    <Popup
                        trigger={<Button icon="ellipsis vertical" size="mini" basic className="post-card-menu-btn" />}
                        on="click"
                        position="bottom right"
                        open={menuOpen}
                        onOpen={() => setMenuOpen(true)}
                        onClose={() => setMenuOpen(false)}
                        content={
                            <div className="post-card-menu">
                                <Button basic size="small" fluid onClick={() => { setMenuOpen(false); onEdit(post); }}>
                                    <Icon name="edit" /> Edit
                                </Button>
                                <Button basic size="small" fluid color="red" onClick={() => { setMenuOpen(false); onDelete(post); }}>
                                    <Icon name="trash" /> Delete
                                </Button>
                            </div>
                        }
                    />
                ) : (
                    <Popup
                        trigger={<Button icon="ellipsis vertical" size="mini" basic className="post-card-menu-btn" />}
                        on="click"
                        position="bottom right"
                        open={menuOpen}
                        onOpen={() => setMenuOpen(true)}
                        onClose={() => setMenuOpen(false)}
                        content={
                            <div className="post-card-menu">
                                {alreadyReported ? (
                                    <Button basic size="small" fluid disabled style={{ color: '#9ca3af' }}>
                                        <Icon name="flag" /> Already reported
                                    </Button>
                                ) : (
                                    <Button basic size="small" fluid onClick={handleReport}>
                                        <Icon name="flag" /> Report
                                    </Button>
                                )}
                            </div>
                        }
                    />
                )}
            </div>

            <div className="post-card-content">
                <p style={{ whiteSpace: 'pre-wrap' }}>{renderContentWithMentions(rawContent, onMentionClick, maxVisible)}</p>
                {canTruncate && (
                    <span
                        className="post-card-read-more"
                        role="button"
                        tabIndex={0}
                        onClick={() => setExpanded(!expanded)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setExpanded(!expanded);
                            }
                        }}
                    >
                        {expanded ? 'Show less' : 'Read more'}
                    </span>
                )}
            </div>

            {post.ImageUrl && (
                <div className="post-card-image-wrapper">
                    <img
                        src={post.ImageUrl}
                        alt="Post"
                        className="post-card-image"
                        role="button"
                        tabIndex={0}
                        onClick={() => setImageModalOpen(true)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setImageModalOpen(true);
                            }
                        }}
                    />
                </div>
            )}

            {post.ImageUrl && (
                <Modal open={imageModalOpen} onClose={() => setImageModalOpen(false)} basic size="large">
                    <Modal.Content image>
                        <Image src={post.ImageUrl} fluid />
                    </Modal.Content>
                    <Modal.Actions>
                        <Button basic inverted onClick={() => setImageModalOpen(false)}>
                            <Icon name="close" /> Close
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}

            <ReportContentModal
                open={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                onSubmit={submitReport}
                loading={reporting}
                contentLabel="post"
            />

            <div className="post-card-actions">
                <Popup
                    trigger={
                        <Button
                            icon
                            size="mini"
                            basic={!likedByMe}
                            color={likedByMe ? 'blue' : undefined}
                            className="post-card-like-btn"
                            onClick={() => onToggleLike?.(post)}
                            aria-label={likedByMe ? 'Unlike' : 'Like'}
                        >
                            <Icon name={likedByMe ? 'heart' : 'heart outline'} />
                            <span className="post-card-like-count">{likeCount > 0 ? likeCount : 'Like'}</span>
                        </Button>
                    }
                    on="hover"
                    position="top center"
                    content={
                        !hasLikes ? (
                            'No likes yet'
                        ) : likers.length === 0 ? (
                            `${likeCount} ${likeCount === 1 ? 'like' : 'likes'}`
                        ) : (
                            <div className="post-card-likers-tooltip">
                                {likers.map(l => {
                                    const likerIdentity = l.LikerType === 'resident'
                                        ? residentIdentityById?.[normalizeId(l.LikerId)]
                                        : undefined;
                                    return (
                                        <div key={`${l.LikerType}-${l.LikerId}`} className="post-card-liker">
                                            <div className="post-card-liker-avatar">
                                                <img src={likerIdentity?.Image || l.LikerImage || '/avatar_placeholder.png'} alt="" />
                                            </div>
                                            <span>{likerIdentity?.Name || l.LikerName}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )
                    }
                />
                <Button
                    icon
                    size="mini"
                    basic={!commentsExpanded}
                    color={commentsExpanded ? 'blue' : undefined}
                    className="post-card-comment-btn"
                    onClick={() => setCommentsExpanded(v => !v)}
                    aria-expanded={commentsExpanded}
                    aria-label="Comments"
                >
                    <Icon name="comment outline" />
                    <span className="post-card-comment-count">{commentCount > 0 ? commentCount : 'Comment'}</span>
                </Button>
            </div>

            {commentsExpanded && (
                // OrderCommentsSection renders a bare .oc-wrapper--card here; on the order
                // surfaces its parent supplies the padded, bordered frame. The post feed has
                // no such parent, so this wrapper carries the gutters, divider and background.
                <div className="post-card-comments">
                    <OrderCommentsSection
                        commentsEnabled
                        facilityId={facilityId}
                        postId={post._id}
                        registrantId={currentResidentId}
                        facilityTimezone={facilityTimezone}
                        onCountChange={setCommentCount}
                    />
                </div>
            )}
        </div>
    );
};

export default PostCard;
